import { FC, useState, useEffect, useCallback } from "react";
import {
  Box,
  Stack,
  Typography,
  Switch,
  CircularProgress,
  useTheme
} from "@mui/material";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { DateTimeRangePicker } from "@vilocnv/allsetra-core";
import { MonitoringService } from "app/data/services";
import {
  MonitoringKPI,
  MonitoringParameter,
  TelemetryData,
  MonitoringEvent
} from "app/data/types";
import KPITilesRow from "./children/KPITilesRow";
import TelemetryChartsGrid from "./children/TelemetryChartsGrid";

interface Props {
  specificObject: any | null;
}

const ObjectMonitoringSection: FC<Props> = ({ specificObject }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [queryRange, setQueryRange] = useState(() => ({
    from: moment().subtract(3, "hours").toISOString(),
    to: moment().toISOString()
  }));
  const [pickerRange, setPickerRange] = useState<{
    startDate?: Date;
    endDate?: Date;
  }>({});

  // Feature States
  const [syncHover, setSyncHover] = useState(false);
  const [hoveredTimestamp, setHoveredTimestamp] = useState<string | null>(null);
  const [lockedTimestamp, setLockedTimestamp] = useState<string | null>(null);

  // Data States
  const [loading, setLoading] = useState(true);
  const [parameters, setParameters] = useState<MonitoringParameter[]>([]);
  const [kpis, setKpis] = useState<MonitoringKPI[]>([]);
  const [telemetry, setTelemetry] = useState<TelemetryData[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<MonitoringEvent | null>(
    null
  );

  const handleMonitoringDateRangeChange = useCallback(
    (value: { startDate?: Date; endDate?: Date }) => {
      if (!value.startDate || !value.endDate) return;
      const minStart = moment().subtract(3, "months").startOf("day");
      const maxEnd = moment().endOf("day");
      let start = moment(value.startDate);
      let end = moment(value.endDate);
      if (start.isBefore(minStart)) start = minStart.clone();
      if (end.isAfter(maxEnd)) end = maxEnd.clone();
      if (start.isAfter(end)) end = start.clone();
      setPickerRange({
        startDate: start.toDate(),
        endDate: end.toDate()
      });
      setQueryRange({
        from: start.toISOString(),
        to: end.toISOString()
      });
    },
    []
  );

  // Fetch Data on criteria change
  useEffect(() => {
    const fetchData = async () => {
      if (!specificObject?.uniqueId) return;
      setLoading(true);

      const from = queryRange.from;
      const to = queryRange.to;

      try {
        const objectId = specificObject.uniqueId;

        // 1. Fetch metadata params
        const params = await MonitoringService.getParameters(objectId);
        setParameters(params);

        // 2. Fetch Telemetry
        const activeParams = params.filter((p) => p.isActiveField);
        const teleData = await MonitoringService.getTelemetryData(
          objectId,
          activeParams,
          from,
          to
        );
        setTelemetry(teleData);

        // 3. Derive KPIs from /timeseries payload
        const kpisData = await MonitoringService.getKPIs(
          objectId,
          from,
          to,
          activeParams,
          teleData
        );
        setKpis(kpisData);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [specificObject?.uniqueId, queryRange.from, queryRange.to]);

  return (
    <Box mt={4} mb={10} px={4}>
      {/* Topbar: Filters and Toggles */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={4}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <DateTimeRangePicker
            name="monitoringDateRange"
            value={pickerRange}
            placeholder="Last 3 hours"
            onChange={handleMonitoringDateRangeChange}
            onSubmit={() => {}}
            theme={theme}
            translator={t}
            maxLookbackMonths={3}
          />

          {/* <Box sx={{ bgcolor: "#FFF", borderRadius: "8px", pl: 1, border: "1px solid #D5D9E8", display: "flex", alignItems: "center" }}>
            <Typography sx={{ color: "#94A3B8", ml: 1, mr: 0.5 }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
            </Typography>
            <input
              type="text"
              placeholder="Search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ border: "none", outline: "none", padding: "8px", width: "200px" }}
            />
          </Box> */}

          <Switch
            checked={syncHover}
            onChange={(e) => setSyncHover(e.target.checked)}
            color="primary"
          />
          <Typography
            variant="body2"
            sx={{ fontWeight: 500, color: "#1D1F2B" }}
          >
            Sync hover across charts
          </Typography>

          {loading && <CircularProgress size={24} />}
        </Stack>
      </Stack>

      {/* {selectedEvent && (
        <Box sx={{ bgcolor: "#FFF3CD", border: "1px solid #FFE69C", borderRadius: "8px", p: 2, mb: 3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="body2" sx={{ fontWeight: 600 }}>Selected event:</Typography>
            <Typography variant="body2">{selectedEvent.description}</Typography>
            <Typography variant="caption" sx={{ color: "#646D7B" }}>{moment(selectedEvent.timestamp).format("MMM DD, hh:mm A")}</Typography>
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={{
              borderRadius: "4px", fontSize: "0.5rem", fontWeight: 600,
              bgcolor: selectedEvent.level === "Severe" ? "#FEE2E2" : selectedEvent.level === "Warning" ? "#FEF3C7" : "#DBEAFE",
              color: selectedEvent.level === "Severe" ? "#EF4444" : selectedEvent.level === "Warning" ? "#F59E0B" : "#3B82F6"
            }}>
              {selectedEvent.level}
            </Box>
            <Box sx={{ cursor: "pointer", display: "flex" }} onClick={() => {
              setSelectedEvent(null);
              setHoveredTimestamp(null);
              setLockedTimestamp(null);
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
            </Box>
          </Stack>
        </Box>
      )}

      <MonitoringEventsTable
        objectId={specificObject?.uniqueId}
        rangeFrom={queryRange.from}
        rangeTo={queryRange.to}
        searchStr={search}
        syncHover={syncHover}
        hoveredTimestamp={hoveredTimestamp}
        setHoveredTimestamp={setHoveredTimestamp}
        onRowClick={(event) => {
          setSelectedEvent(event);
          setHoveredTimestamp(event.timestamp);
          setLockedTimestamp(event.timestamp);
        }}
      /> */}

      <KPITilesRow kpis={kpis} loading={loading} />

      <TelemetryChartsGrid
        parameters={parameters}
        telemetry={telemetry}
        loading={loading}
        rangeFrom={queryRange.from}
        rangeTo={queryRange.to}
        syncHover={syncHover}
        hoveredTimestamp={hoveredTimestamp}
        setHoveredTimestamp={(ts) => {
          if (!selectedEvent) setHoveredTimestamp(ts);
        }}
        clickedTimestamp={lockedTimestamp || selectedEvent?.timestamp || null}
        onToggleClickTimestamp={(ts) => {
          setLockedTimestamp((current) => (current === ts ? null : ts));
          setSelectedEvent(null);
        }}
      />
    </Box>
  );
};

export default ObjectMonitoringSection;
